import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';
import { isPureSyndicator } from '@core/authentication/auth.utility';

/**
 * Keeps pure syndicators (SYNDICATOR / MASTER_SYNDICATOR with no other role) out of the v2
 * home shell — they have a dedicated v1 area at `/syndicator` and no v2 home navigation.
 *
 * Placed on the `/v2/home` parent route so it covers the whole `/v2/home/**` subtree, for
 * both direct navigation and login `returnUrl` deep links (e.g. `/v2/home/queues`,
 * `/v2/home/collections`, `/v2/home/finance`, `/v2/home/syndications`).
 *
 * Multi-role users (a syndicator role AND an internal role) are intentionally left alone so
 * they keep access to their v2 UI; their landing is resolved by the normal role flow.
 */
export const syndicatorHomeRedirectGuard: CanActivateFn = (): boolean | UrlTree => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const user = auth.getUser();
  if (!user) {
    // Not signed in — defer to the authentication guard on the same route.
    return true;
  }

  const roles = (user.roles || []).map((r: { name: string }) => r.name);
  return isPureSyndicator(roles) ? router.parseUrl('/syndicator') : true;
};
